import React, { useCallback, useEffect, useState } from "react";
import "./LastProjects.css";
import { UilAngleLeftB, UilAngleRightB } from "@iconscout/react-unicons";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { BeatLoader } from "react-spinners";
import { toast } from "react-toastify";
import { resolveUrl } from "../../../utils/storeUtils";

const LastProjects = () => {
  const [projects, setProjects] = useState([]);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showDeleteAllConfirmation, setShowDeleteAllConfirmation] =
    useState(false);
  const [selectedProjectId, setSelectedProjectId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exportLoading, setExportLoading] = useState(false);
  const [deleteAllLoading, setDeleteAllLoading] = useState(false);
  const [lang] = useState(localStorage.getItem("lang") || `"ar"`);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [exportCategory, setExportCategory] = useState("All");

  const token =
    localStorage.getItem("authToken") || sessionStorage.getItem("authToken");

  const navigate = useNavigate();

  const fetchProjects = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/projects?page=${currentPage}`);
      setProjects(response?.data?.projects || []);
      setTotalPages(response?.data?.totalPages || 0);
    } catch (error) {
      console.error("Error fetching projects:", error);
      toast.error("Could not load projects.");
    } finally {
      setLoading(false);
    }
  }, [currentPage]);

  useEffect(() => {
    if (!token) {
      navigate("/dashboard/login");
    }
  }, [token, navigate]);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  const handleDeleteConfirm = (projectId) => {
    setShowDeleteAllConfirmation(false);
    setSelectedProjectId(projectId);
    setShowConfirmation(true);
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`/projects/${selectedProjectId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setProjects(
        projects.filter((project) => project._id !== selectedProjectId)
      );
      toast.success("Project has been deleted.");
      setShowConfirmation(false);
      setSelectedProjectId(null);
    } catch (error) {
      console.error("Error deleting project:", error);
      toast.error(
        error.response?.data?.message || "An error occurred while deleting."
      );
    }
  };

  const handleDeleteAllProjects = async () => {
    if (!token) {
      toast.error("Please sign in again.");
      return;
    }
    try {
      setDeleteAllLoading(true);
      const { data } = await axios.delete("/projects/all", {
        headers: { Authorization: `Bearer ${token}` },
      });
      setShowDeleteAllConfirmation(false);
      setCurrentPage(1);
      const listRes = await axios.get("/projects?page=1");
      setProjects(listRes?.data?.projects || []);
      setTotalPages(listRes?.data?.totalPages || 0);
      toast.success(
        data?.message || `Deleted ${data?.deletedCount ?? 0} project(s).`
      );
    } catch (error) {
      console.error("Error deleting all projects:", error);
      toast.error(
        error.response?.data?.message || "Could not delete all projects."
      );
    } finally {
      setDeleteAllLoading(false);
    }
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const downloadBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const handleExportExcel = async () => {
    if (!token) {
      toast.error("Please sign in again.");
      return;
    }
    try {
      setExportLoading(true);
      const qp =
        exportCategory && exportCategory !== "All"
          ? `?category=${encodeURIComponent(exportCategory)}`
          : "";
      const res = await axios.get(`/projects/export/excel${qp}`, {
        responseType: "blob",
        headers: { Authorization: `Bearer ${token}` },
      });
      const cd = res.headers["content-disposition"] || "";
      const match = cd.match(/filename="?([^";]+)"?/i);
      const name =
        match?.[1]?.trim() ||
        `projects-export-${new Date().toISOString().slice(0, 10)}.xlsx`;
      downloadBlob(res.data, name);
      toast.success("Excel download started.");
    } catch (error) {
      console.error(error);
      toast.error(
        error.response?.data?.message || "Could not export projects to Excel."
      );
    } finally {
      setExportLoading(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return "-";
    const d = new Date(date);
    if (Number.isNaN(d.getTime())) return "-";
    return `${d.getDate()}/${d.getMonth() + 1}/${d.getFullYear()}`;
  };

  const projectThumb = (project) => {
    const u = project?.image?.url;
    return u ? resolveUrl(u, axios.defaults.baseURL) : "";
  };

  if (loading && !projects.length && currentPage === 1) {
    return (
      <div className="loader-container">
        <BeatLoader />
      </div>
    );
  }

  const busyExport = exportLoading || loading || deleteAllLoading;

  return (
    <>
      <header className="lastProjectsHeader" />
      <main className="lastProjects lastProjects--projects">
        <div className="container">
          <div className="title">
            <div className="title-heading">
              <h1>Projects</h1>
              <span className="title-count">
                {projects.length} on this page
                {totalPages > 1 ? ` · page ${currentPage}/${totalPages}` : ""}
              </span>
            </div>
          </div>

          <div
            className="projects-bulk-toolbar projects-bulk-toolbar--export-only projects-bulk-toolbar--below-title"
            aria-label="Export, delete all, and bulk import"
          >
            <div className="projects-bulk-toolbar__export">
              <label className="projects-export-filter" aria-label="Export category">
                <span className="projects-export-filter__label">Category</span>
                <select
                  className="projects-export-filter__select"
                  value={exportCategory}
                  onChange={(e) => setExportCategory(e.target.value)}
                  disabled={busyExport}
                >
                  <option value="All">All</option>
                  <option value="Work Space">Work Space</option>
                  <option value="Education">Education</option>
                  <option value="Residential">Residential</option>
                  <option value="Entertainment">Entertainment</option>
                  <option value="Commercial">Commercial</option>
                  <option value="Hospitality">Hospitality</option>
                </select>
              </label>
              <button
                type="button"
                className="projects-bulk-btn projects-bulk-btn--primary"
                disabled={busyExport}
                onClick={handleExportExcel}
              >
                {exportLoading
                  ? "Exporting…"
                  : exportCategory && exportCategory !== "All"
                    ? "Download Excel (category)"
                    : "Download Excel (all)"}
              </button>
              <button
                type="button"
                className="projects-bulk-btn projects-bulk-btn--danger"
                disabled={busyExport}
                onClick={() => {
                  setShowConfirmation(false);
                  setShowDeleteAllConfirmation(true);
                }}
              >
                Delete all projects
              </button>
            </div>
            <Link
              to="/dashboard/add-project"
              className="projects-bulk-btn projects-bulk-btn--primary projects-bulk-add-link"
            >
              + Add project
            </Link>
          </div>

          <div className="table-container">
            <div className="table table--projects-list">
              <div className="head">
                <div className="col">
                  <div className="col-id">#</div>
                  <div>Photo</div>
                  <div>Name</div>
                  <div>Category</div>
                  <div>Created</div>
                  <div className="col-actions">Actions</div>
                </div>
              </div>
              <div className="body">
                {projects.length === 0 ? (
                  <div className="list-empty">
                    <i className="uil uil-folder-open" />
                    <p>No projects on this page.</p>
                  </div>
                ) : (
                  projects.map((project, index) => {
                    const thumb = projectThumb(project);
                    return (
                      <div className="col row" key={project._id}>
                        <div className="col-id">
                          #{(currentPage - 1) * 10 + index + 1}
                        </div>
                        <div className="col-photo">
                          {thumb ? (
                            <img
                              src={thumb}
                              alt={project?.image?.alt || project?.name || ""}
                              className="table-product-thumb"
                              loading="lazy"
                            />
                          ) : (
                            <span className="table-product-thumb-icon">
                              <i className="uil uil-image-v" />
                            </span>
                          )}
                        </div>
                        <div
                          className="col-name"
                          title={
                            project?.name ||
                            project?.arName ||
                            ""
                          }
                        >
                          {project?.name ||
                            project?.arName ||
                            "—"}
                        </div>
                        <div className="col-category">
                          {project?.category ||
                            project?.arCategory ||
                            "—"}
                        </div>
                        <div className="col-date">
                          {formatDate(project?.createdAt)}
                        </div>
                        <div className="actionIcons">
                          <button
                            type="button"
                            className="action-btn action-btn--delete"
                            onClick={() => handleDeleteConfirm(project?._id)}
                            title="Delete"
                            aria-label={`Delete ${project?.name || "project"}`}
                          >
                            <i className="uil uil-trash-alt" />
                          </button>
                          <Link
                            to={`/dashboard/edit-project/${project?._id}`}
                            className="action-btn action-btn--edit"
                            title="Edit"
                            aria-label={`Edit ${project?.name || "project"}`}
                          >
                            <i className="uil uil-edit-alt" />
                          </Link>
                        </div>
                      </div>
                    );
                  })
                )}

                {showConfirmation && (
                  <div className="confirmation-modal">
                    <div className="modal-content sec">
                      <img src="/photos/question.svg" alt="" />
                      <p>Are you sure you want to delete this project?</p>
                      <div className="modal-actions">
                        <button type="button" onClick={handleDelete}>
                          Yes
                        </button>
                        <button
                          type="button"
                          onClick={() => setShowConfirmation(false)}
                        >
                          No
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {showDeleteAllConfirmation && (
                  <div className="confirmation-modal">
                    <div className="modal-content sec projects-delete-all-modal">
                      <img src="/photos/question.svg" alt="" />
                      <p>
                        Delete <strong>all</strong> projects? This cannot be
                        undone. Image files on the server will be removed when
                        found.
                      </p>
                      <div className="modal-actions">
                        <button
                          type="button"
                          className="projects-delete-all-confirm"
                          disabled={deleteAllLoading}
                          onClick={handleDeleteAllProjects}
                        >
                          {deleteAllLoading ? "Deleting…" : "Delete everything"}
                        </button>
                        <button
                          type="button"
                          disabled={deleteAllLoading}
                          onClick={() => setShowDeleteAllConfirmation(false)}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="pagination" dir="ltr">
                {currentPage > 1 && (
                  <button
                    type="button"
                    onClick={() => handlePageChange(currentPage - 1)}
                    className="pagination-nav active"
                    aria-label="Previous page"
                  >
                    {lang === "en" ? (
                      <UilAngleRightB size={32} color="white" />
                    ) : (
                      <UilAngleLeftB size={32} color="white" />
                    )}
                  </button>
                )}

                {Array.from({ length: totalPages }, (_, index) => index + 1)
                  .filter((pageNumber) => {
                    if (pageNumber === 1 || pageNumber === totalPages)
                      return true;
                    if (
                      pageNumber >= currentPage - 1 &&
                      pageNumber <= currentPage + 1
                    ) {
                      return true;
                    }
                    return false;
                  })
                  .map((pageNumber, index, filteredPages) => (
                    <React.Fragment key={pageNumber}>
                      {index > 0 &&
                        pageNumber !== filteredPages[index - 1] + 1 && (
                          <span className="dots">…</span>
                        )}
                      <button
                        type="button"
                        onClick={() => handlePageChange(pageNumber)}
                        className={
                          currentPage === pageNumber ? "active" : undefined
                        }
                      >
                        {pageNumber}
                      </button>
                    </React.Fragment>
                  ))}

                {currentPage < totalPages && (
                  <button
                    type="button"
                    onClick={() => handlePageChange(currentPage + 1)}
                    className="pagination-nav active"
                    aria-label="Next page"
                  >
                    {lang === `"ar"` ? (
                      <UilAngleRightB size={32} color="white" />
                    ) : (
                      <UilAngleLeftB size={32} color="white" />
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>
    </>
  );
};

export default LastProjects;
